import { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import validations from "../utils/validationFunctions";

/* 
    Example Props
const form = {
  initialValues: {
    name: "",
    username: "",
  },
  validation: {
    name: {
      required: { msg: "" },
    },
  },
}; */

const useForm = ({ initialState, validation = {}, callback }) => {
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [values, setValues] = useState(initialState);

  const { t } = useTranslation();
  const didMount = useRef(false);

  const handleChange = ({ target: { name, value } }) => {
    setValue(name, value);
  };

  const setValue = (name, value) => {
    setMultipleValues([{ name, value }]);
  };

  const setMultipleValues = (items = []) => {
    setValues((prev) => {
      const state = {
        ...prev,
        ...items.reduce((acc, { name, value }) => {
          acc[name] = value;
          return acc;
        }, {}),
      };

      if (submitted) validate(state);
      return state;
    });
  };

  const validate = (data) => {
    const newErrors = Object.keys(validation).reduce((acc, name) => {
      Object.keys(validation[name]).some((key) => {
        // validations
        const isInvalid = validations[key]({
          value: data[name],
          ...validation[name][key]?.parameters,
        });

        if (!isInvalid) return false;
        acc[name] =
          validation[name][key]?.msg || t(`general.formErrors.${key}`);
        return true;
      });
      return acc;
    }, {});

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onSubmit = (e) => {
    e && e.preventDefault();
    setSubmitted(true);
    if (validate(values)) callback(values);
  };

  const resetValues = () => {
    setErrors({});
    setValues(initialState);
    setSubmitted(false);
  };

  const setError = (name, value) => {
    setErrors((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    if (didMount.current) resetValues();
    else didMount.current = true;
  }, [JSON.stringify(initialState)]);

  return {
    errors,
    values,
    resetValues,
    onSubmit,
    handleChange,
    setValue,
    setMultipleValues,
    setError,
  };
};

export default useForm;
